import { createInstance, i18n as I18nInstance } from "i18next";
import { initReactI18next } from "react-i18next";
import { ENABLED_APP_LOCALES, registerAppLocaleConfig, type EnabledAppLocale } from "./localeRegistry";
import { appNamespaces, defaultNamespace, englishResources, loadLocaleBundle } from "./resources";
import { asEnabledAppLocale, getInitialLocaleResolution } from "./localeResolver";

export const createAppI18n = (): I18nInstance => {
    const instance = createInstance();
    void instance.use(initReactI18next).init({
        resources: { en: englishResources },
        lng: "en",
        fallbackLng: "en",
        partialBundledLanguages: true,
        supportedLngs: [...ENABLED_APP_LOCALES],
        defaultNS: defaultNamespace,
        ns: [...appNamespaces],
        load: "currentOnly",
        returnNull: false,
        initAsync: false,
        interpolation: {
            escapeValue: false,
        },
        react: {
            useSuspense: false,
        },
    });
    return instance;
};

export const initialLocaleResolution = getInitialLocaleResolution();
export const appI18n = createAppI18n();

export const loadAppLocale = async (instance: I18nInstance, locale: EnabledAppLocale): Promise<void> => {
    if (locale === "en" || appNamespaces.every((namespace) => instance.hasResourceBundle(locale, namespace))) {
        return;
    }

    const bundle = await loadLocaleBundle(locale);
    registerAppLocaleConfig(bundle.config);
    for (const namespace of appNamespaces) {
        const catalog = bundle.resources[namespace];
        if (!catalog) {
            throw new Error(`${locale}: locale bundle is missing the ${namespace} namespace.`);
        }
        instance.addResourceBundle(locale, namespace, catalog, true, true);
    }
};

export const changeAppLanguage = async (instance: I18nInstance, locale: EnabledAppLocale): Promise<void> => {
    await loadAppLocale(instance, locale);
    await instance.changeLanguage(locale);
};

export const createLoadedAppI18n = async (locale: EnabledAppLocale = "en"): Promise<I18nInstance> => {
    const instance = createAppI18n();
    await changeAppLanguage(instance, locale);
    return instance;
};

export const appI18nReady = changeAppLanguage(appI18n, initialLocaleResolution.locale).catch(async (error: unknown) => {
    await appI18n.changeLanguage("en");
    throw error;
});

export const getI18nLocale = (instance: I18nInstance = appI18n): EnabledAppLocale => {
    return asEnabledAppLocale(instance.resolvedLanguage ?? instance.language) ?? "en";
};
